// @flow

import * as React from 'react';

import * as Sentry from '@sentry/react';

import type { TranslatorProps } from '@gandi/translate';
import { Base, Button } from '@gandi/ui';

import type { GandiLogger } from 'gandi.redux-logger';

import { withRouter } from '../../../../helpers/withRouter';
import type { WithRouterProps } from '../../../../helpers/withRouter';
import type {
  GlobalErrors,
  InnerStep,
  InnerStepFlag,
  Process,
  Role,
  Step,
  Tld,
  TrusteePrices,
  UIStep,
} from '../types';

import RadioOption from './RadioOption';
import type { Option } from './RadioOption';

type OwnProps = {
  backUrl: string,
  cartId: string,
  domainCount: number,
  globalErrors: GlobalErrors,
  tld: Tld,
  translator: TranslatorProps,
  logger: GandiLogger,
  removeProductHandler: (evt: SyntheticEvent<>) => Promise<mixed | null>,
  setInnerStepFlag: (
    innerStep: InnerStep,
    flag: InnerStepFlag,
    roleHasTrustee: boolean,
  ) => Promise<void>,
  setTrusteeRoleFlag: Role => Promise<void>,
  handleSubmitTrustee: () => Promise<void>,
  trusteePrices: ?TrusteePrices,
  role: Role,
  uiStep: UIStep,
  isDeleting: boolean,
};

export type InjectedProps = WithRouterProps<>;

export type SelectionScreenProps = {
  ...OwnProps,
  ...InjectedProps,
};

type State = {
  checked: string,
};

const getDeleteMessage =
  (__: TranslatorProps['__']) => (uiStep: ?UIStep | ?Step) => {
    if (uiStep) {
      const { tld, process } = uiStep.domainInfo;

      const mapping: { [Process]: string, ... } = {
        create: __(
          'Remove all .%(tld)s products for ' +
            'create process from your shopping cart.',
          {
            tld,
          },
        ),
        transfer: __(
          'Remove all .%(tld)s products for ' +
            'transfer process from your shopping cart.',
          {
            tld,
          },
        ),
        change_owner: __(
          'Remove all .%(tld)s products for ' +
            'change owner process from your shopping cart.',
          {
            tld,
          },
        ),
      };
      // $FlowFixMe[incompatible-type]
      return mapping[process] || '';
    }
    return '';
  };

class SelectionScreen extends React.PureComponent<SelectionScreenProps, State> {
  state: State = {
    checked:
      this.props.globalErrors[this.props.uiStep.purchaseGroupId] &&
      this.props.globalErrors[this.props.uiStep.purchaseGroupId][this.props.tld]
        ?.owner_type_error
        ? 'account_choice'
        : 'edit',
  };

  /**
   * Handle radio selection
   */
  handleChange: (evt: SyntheticInputEvent<>) => void = (
    evt: SyntheticInputEvent<>,
  ) => {
    this.setState({ checked: evt.target.value });
  };

  handleClick: (evt: SyntheticEvent<HTMLButtonElement>) => void = (
    evt: SyntheticEvent<HTMLButtonElement>,
  ) => {
    evt.preventDefault();
    const roleHasTrustee: boolean = !!(
      this.props.tld &&
      this.props.globalErrors[this.props.uiStep.purchaseGroupId] &&
      this.props.globalErrors[this.props.uiStep.purchaseGroupId][
        this.props.tld
      ] &&
      this.props.globalErrors[this.props.uiStep.purchaseGroupId][
        this.props.tld
      ].trustee_roles.includes(this.props.role)
    );

    const uiStep = this.props.uiStep;
    const lastUiStepRole = uiStep.roles[uiStep.roles.length - 1];

    switch (this.state.checked) {
      case 'edit': {
        // Transition 'menu' -> 'form'
        this.props.setInnerStepFlag(
          {
            purchaseGroupId: uiStep.purchaseGroupId,
            tld: uiStep.domainInfo.tld,
            // $FlowFixMe[incompatible-call]
            process: uiStep.domainInfo.process,
            role: lastUiStepRole.label,
          },
          'form',
          roleHasTrustee,
        );
        break;
      }
      case 'trustee': {
        this.props.setTrusteeRoleFlag(lastUiStepRole.label);
        this.props.handleSubmitTrustee();
        break;
      }
      case 'account_choice':
        // eslint-disable-next-line react/prop-types
        this.props.navigate(
          `/cart/${this.props.cartId}/configure/account-choice`,
        );
        break;
      case 'remove':
        this.props.removeProductHandler(evt);
        break;
      default:
    }
  };

  renderOptions: () => Array<React.MixedElement> = () => {
    const needAccountChoice =
      this.props.tld &&
      this.props.globalErrors[this.props.uiStep.purchaseGroupId] &&
      this.props.globalErrors[this.props.uiStep.purchaseGroupId][this.props.tld]
        ? this.props.globalErrors[this.props.uiStep.purchaseGroupId][
            this.props.tld
          ].owner_type_error
        : false;
    const roleHasTrustee =
      this.props.globalErrors[this.props.uiStep.purchaseGroupId] &&
      this.props.globalErrors[this.props.uiStep.purchaseGroupId][
        this.props.tld
      ] &&
      this.props.globalErrors[this.props.uiStep.purchaseGroupId][
        this.props.tld
      ].trustee_roles.includes(this.props.role);
    const deleteMessage = getDeleteMessage(this.props.translator.__)(
      this.props.uiStep,
    );

    /** Prices are _sometimes_ indexed by TLD in billing, and returned as SLD+TLD
     * by suggest so pretty much everywhere inside the wizard.\
     * We will implement something cleaner e.g. return full_tld in suggest.
     */
    const tldLastPart = this.props.tld.split('.').pop();

    let price =
      (roleHasTrustee &&
        tldLastPart &&
        (this.props.trusteePrices?.prices[this.props.tld]?.price_after_taxes ||
          this.props.trusteePrices?.prices[tldLastPart]?.price_after_taxes)) ||
      undefined;

    if (!price && price !== 0) {
      price = undefined;
    }

    const currency = this.props.trusteePrices?.currency ?? '';
    const showTrustee =
      roleHasTrustee &&
      ['create', 'transfer'].includes(this.props.uiStep.domainInfo.process);

    let content;
    switch (price) {
      case 0:
        content = this.props.translator.__(
          'Subscribe to a free trustee for your domains',
        );
        break;
      case undefined: {
        content = this.props.translator.__(
          'Subscribe to a trustee Pricing Unavailable.',
        );

        if (showTrustee) {
          const trusteePriceError = new Error(
            `Trustee price missing for TLD ${this.props.tld}`,
          );
          Sentry.captureException(trusteePriceError, {
            tld: this.props.tld,
          });
          this.props.logger.error(trusteePriceError.message);
        }
        break;
      }
      default:
        content = this.props.translator.__(
          'Subscribe to a trustee %(amount)s /domain in total %(total)s for this order',
          {
            amount: this.props.translator.formatPrice(price, currency),
            total: this.props.translator.formatPrice(
              (price * 100 * this.props.domainCount) / 100,
              currency,
            ),
          },
        );
    }
    const options = [
      !needAccountChoice && {
        value: 'edit',
        content: this.props.translator.__('Edit contacts information'),
        help: this.props.translator.__(
          'We will apply these information to all the .%(tld)s domains',
          { tld: this.props.tld },
        ),
      },
      needAccountChoice && {
        value: 'account_choice',
        content: this.props.translator.__(
          'Go back and change owner organization from the Account Choice menu',
        ),
        help: this.props.translator.__(
          'You will have to pick a different organization and this will change ' +
            'the owner of the cart...',
          { tld: this.props.tld },
        ),
      },
      showTrustee && {
        value: 'trustee',
        content,
        help: this.props.translator.__(
          'A trustee is a legal presence in the right country ' +
            'to satisfy the registry requirements.',
        ),
      },
      {
        value: 'remove',
        content: deleteMessage,
        help: this.props.translator.__(
          'If you are not able to fill the right information and ' +
            "you don't really need these domain names.",
        ),
      },
    ];

    const cleanOptions: Array<Option> = (options.filter(Boolean): Array<any>);

    return cleanOptions.map((option, index) => (
      <RadioOption
        option={option}
        index={index}
        key={`radio-option-${option.value}`}
        handler={this.handleChange}
      />
    ));
  };

  renderButtons: () => React.Node = () => (
    <Base marginBottom="base" marginTop="base" flex flexWrap>
      <Button to={this.props.backUrl} grouped kind="outline" type="button">
        {this.props.translator.__('Cancel')}
      </Button>
      <Button
        grouped
        type="button"
        onClick={this.handleClick}
        disabled={this.props.isDeleting}
      >
        {this.props.translator.__('Continue')}
      </Button>
    </Base>
  );

  render(): React.Node {
    return (
      <Base padding="small" backgroundColor="surface">
        {this.renderOptions()}
        {this.renderButtons()}
      </Base>
    );
  }
}

export default (withRouter(SelectionScreen): React.ComponentType<OwnProps>);
